<template>
  <div>
    <v-container fluid>
      <v-card elevation="3" rounded="xl">
        <v-card-title class="py-4 px-6">
          <v-row class="w-100 ma-0" align="center">
            <v-col cols="12" md="6" class="pa-0">
              <h2 class="text-h5 font-weight-bold primary--text">RMC Plants</h2>
              <p class="text-body-2 grey--text ma-0">Manage all registered RMC plants</p>
            </v-col>
            <v-col cols="12" md="6" class="pa-0 text-right">
              <v-chip color="primary" outlined class="mr-3">
                <v-icon left small>mdi-factory</v-icon>
                {{ count }} Plants
              </v-chip>
            </v-col>
          </v-row>
        </v-card-title>

        <v-divider class="mx-6"></v-divider>

        <v-card-subtitle class="px-6 pt-4 pb-2">
          <v-row>
            <v-col cols="12" md="4">
              <v-text-field v-model="filter.search" label="Search plants..." prepend-inner-icon="mdi-magnify" outlined rounded dense clearable @keydown.enter="getData" @click:clear="filter.search = ''; getData()"></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <v-select v-model="filter.city" :items="cities" label="City" prepend-inner-icon="mdi-map-marker" outlined rounded dense clearable @change="getData"></v-select>
            </v-col>
            <v-col cols="12" md="3">
              <v-select v-model="filter.status" :items="statuses" label="Status" prepend-inner-icon="mdi-toggle-switch" outlined rounded dense clearable @change="getData"></v-select>
            </v-col>
          </v-row>
        </v-card-subtitle>

        <v-card-text class="pa-0">
          <table-skeleton v-if="isLoading" :rows="8" :cols="6" />

          <v-data-table v-else :headers="headers" :items="plants" hide-default-footer class="enhanced-table">
            <template #[`item.sr_no`]="{ index }">
              <span class="font-weight-medium primary--text">{{ index + 1 }}</span>
            </template>

            <template #[`item.listed`]="{ item }">
              <v-switch
                v-model="item.is_listed"
                color="success"
                dense
                hide-details
                :loading="togglingId === item.id"
                @change="toggleListing(item)"
              ></v-switch>
            </template>

            <template #[`item.status`]="{ item }">
              <v-chip :color="item.status === 'active' ? 'success' : 'warning'" small dark>
                {{ item.status }}
              </v-chip>
            </template>

            <template #[`item.name`]="{ item }">
              <div>
                <div class="font-weight-medium">{{ item.name }}</div>
                <div class="text-caption grey--text">{{ item.city }}, {{ item.state }}</div>
              </div>
            </template>

            <template #[`item.capacity`]="{ item }">
              <div>
                <div>{{ formatNumber(item.total_capacity) }} m³/mo</div>
                <v-progress-linear :value="item.usage_percentage" :color="item.usage_percentage > 80 ? 'error' : item.usage_percentage > 40 ? 'warning' : 'success'" height="6" rounded class="mt-1"></v-progress-linear>
                <div class="text-caption grey--text">{{ item.usage_percentage }}% used</div>
              </div>
            </template>

            <template #[`item.price`]="{ item }">
              <span class="font-weight-medium">₹{{ formatNumber(item.price_per_cubic_meter) }}/m³</span>
            </template>

            <template #[`item.owner`]="{ item }">
              <span v-if="item.owner">{{ item.owner.first_name || item.owner.user_name }}</span>
              <span v-else class="grey--text">—</span>
            </template>

            <template #[`item.rating`]="{ item }">
              <div class="d-flex align-center">
                <v-icon small color="amber">mdi-star</v-icon>
                <span class="ml-1">{{ item.rating }}</span>
                <span class="text-caption grey--text ml-1">({{ item.review_count }})</span>
              </div>
            </template>

            <template #[`item.actions`]="{ item }">
              <v-btn small icon color="primary" class="mr-1" @click="openEdit(item)">
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
              <v-btn small color="error" icon @click="deletePlant(item.id)">
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </template>

            <template #no-data>
              <div class="text-center py-12">
                <v-icon size="60" color="grey lighten-3">mdi-factory</v-icon>
                <h3 class="grey--text mt-4">No Plants Found</h3>
              </div>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-container>

    <!-- Edit Plant Dialog -->
    <v-dialog v-model="showEditDialog" max-width="520">
      <v-card rounded="lg" v-if="editPlant">
        <v-card-title class="pa-6 pb-3">
          <v-icon left color="primary">mdi-pencil</v-icon>
          Edit Plant
        </v-card-title>
        <v-card-text class="px-6 pb-2">
          <v-text-field v-model="editPlant.name" label="Plant Name" outlined dense class="mb-2"></v-text-field>
          <v-row>
            <v-col cols="6">
              <v-text-field v-model="editPlant.city" label="City" outlined dense></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field v-model="editPlant.state" label="State" outlined dense></v-text-field>
            </v-col>
          </v-row>
          <v-text-field v-model="editPlant.address" label="Address" outlined dense class="mb-2"></v-text-field>
          <v-row>
            <v-col cols="4">
              <v-text-field v-model="editPlant.price_per_cubic_meter" label="Plant Price/m³ (₹)" type="number" outlined dense></v-text-field>
            </v-col>
            <v-col cols="4">
              <v-text-field v-model="editPlant.commission_per_cubic_m" label="Commission/m³ (₹)" type="number" outlined dense hint="Platform markup added to client price" persistent-hint></v-text-field>
            </v-col>
            <v-col cols="4">
              <v-text-field v-model="editPlant.transport_rate_per_m3" label="Transport Rate/m³ (₹)" type="number" outlined dense hint="Charged per m³ for delivery" persistent-hint></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="pt-0">
              <v-alert dense text color="info" class="text-caption mb-2">
                Client sees: ₹{{ ((parseFloat(editPlant.price_per_cubic_meter) || 0) + (parseFloat(editPlant.commission_per_cubic_m) || 0)).toFixed(2) }}/m³ concrete + ₹{{ (parseFloat(editPlant.transport_rate_per_m3) || 0).toFixed(2) }}/m³ transport
              </v-alert>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <v-select v-model="editPlant.operating_start_time" :items="hourOptions" item-text="label" item-value="value" label="Opening Time" outlined dense hint="Plant opens at" persistent-hint></v-select>
            </v-col>
            <v-col cols="6">
              <v-select v-model="editPlant.operating_end_time" :items="hourOptions" item-text="label" item-value="value" label="Closing Time" outlined dense hint="Plant closes at" persistent-hint></v-select>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <v-select v-model="editPlant.status" :items="statuses" label="Status" outlined dense></v-select>
            </v-col>
            <v-col cols="6">
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <v-text-field v-model="editPlant.total_capacity" label="Total Capacity (m³)" type="number" outlined dense></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field v-model="editPlant.batching_capacity" label="Batching Capacity (TPH)" type="number" outlined dense></v-text-field>
            </v-col>
          </v-row>
          <v-switch v-model="editPlant.is_listed" label="Listed (visible to clients)" color="success" dense hide-details class="mt-0"></v-switch>

          <!-- Mix Design Presets -->
          <v-divider class="my-4"></v-divider>
          <div class="d-flex align-center justify-space-between mb-2">
            <span class="text-subtitle-2 font-weight-bold">Mix Design Presets</span>
            <v-btn x-small color="primary" outlined @click="openAddGrade">
              <v-icon left x-small>mdi-plus</v-icon> Add Grade
            </v-btn>
          </div>
          <div v-if="!editPlant.mix_design_presets || Object.keys(editPlant.mix_design_presets).length === 0" class="text-caption grey--text mb-2">
            No presets configured. Builders will see standard IS-456 defaults.
          </div>
          <v-expansion-panels v-else flat accordion dense class="mb-2">
            <v-expansion-panel v-for="(ingredients, grade) in editPlant.mix_design_presets" :key="grade" style="border: 1px solid #e0e0e0; border-radius: 8px; margin-bottom: 4px;">
              <v-expansion-panel-header class="py-2 px-3">
                <span class="font-weight-medium primary--text">{{ grade }}</span>
                <template #actions>
                  <v-btn icon x-small color="error" class="mr-1" @click.stop="removeGrade(grade)">
                    <v-icon x-small>mdi-delete</v-icon>
                  </v-btn>
                  <v-icon>$expand</v-icon>
                </template>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row dense class="pt-2">
                  <v-col cols="6" v-for="ing in ingredientOrder" :key="ing">
                    <v-text-field :label="ing + ' (kg)'" :value="ingredients[ing] !== undefined ? ingredients[ing] : 0" type="number" dense outlined @input="v => updateIngredient(grade, ing, v)" class="text-caption"></v-text-field>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
        <v-card-actions class="px-6 pb-6">
          <v-spacer></v-spacer>
          <v-btn text @click="showEditDialog = false">Cancel</v-btn>
          <v-btn color="primary" rounded :loading="isSaving" @click="saveEdit">Save Changes</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add Grade Dialog -->
    <v-dialog v-model="showAddGradeDialog" max-width="400">
      <v-card rounded="lg">
        <v-card-title class="pa-5 pb-3">Add Mix Design Grade</v-card-title>
        <v-card-text class="px-5 pb-2">
          <v-text-field v-model="newGrade.name" label="Grade (e.g. M25)" outlined dense class="mb-3" @keydown.enter="submitAddGrade"></v-text-field>
          <v-row dense>
            <v-col cols="6" v-for="ing in ingredientOrder" :key="ing">
              <v-text-field :label="ing + ' (kg)'" v-model.number="newGrade.ingredients[ing]" type="number" dense outlined class="text-caption"></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="px-5 pb-5">
          <v-spacer></v-spacer>
          <v-btn text @click="showAddGradeDialog = false">Cancel</v-btn>
          <v-btn color="primary" rounded @click="submitAddGrade">Add</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
export default {
  name: "RmcPlantsPage",
  data() {
    return {
      plants: [],
      count: 0,
      isLoading: false,
      isSaving: false,
      togglingId: null,
      filter: { search: "", city: "", status: "" },
      showEditDialog: false,
      editPlant: null,
      showAddGradeDialog: false,
      newGrade: { name: '', ingredients: {} },
      ingredientOrder: ['Cement', 'Flyash', 'Microsilica', 'C-Sand', '20mm', '10mm', 'Water', 'Admixture'],
      cities: ["Navi Mumbai", "Thane", "Pune", "Panvel"],
      statuses: ["active", "maintenance"],
      hourOptions: Array.from({ length: 24 }, (_, i) => ({
        value: `${String(i).padStart(2, '0')}:00`,
        label: i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`,
      })),
      headers: [
        { text: "#", value: "sr_no", sortable: false, width: "50px" },
        { text: "Listed", value: "listed", sortable: false, width: "80px" },
        { text: "Status", value: "status", width: "110px" },
        { text: "Plant", value: "name", width: "220px" },
        { text: "Capacity", value: "capacity", width: "180px" },
        { text: "Price", value: "price", width: "130px" },
        { text: "Owner", value: "owner", width: "150px" },
        { text: "Rating", value: "rating", width: "120px" },
        { text: "", value: "actions", sortable: false, width: "80px" },
      ],
    };
  },
  mounted() {
    this.getData();
  },
  methods: {
    async getData() {
      this.isLoading = true;
      try {
        const params = {};
        if (this.filter.search) params.search_keyword = this.filter.search;
        if (this.filter.city) params.city = this.filter.city;
        if (this.filter.status) params.status = this.filter.status;
        const query = new URLSearchParams(params);
        const response = await axios.get(`rmc_plants?${query}`);
        this.plants = (response.data.data || []).map(p => ({
          ...p,
          is_listed: p.is_listed !== false,
        }));
        this.count = response.data.count || 0;
      } catch (e) {
        console.error("Error fetching plants:", e);
      }
      this.isLoading = false;
    },
    async toggleListing(item) {
      this.togglingId = item.id;
      try {
        await axios.post(`rmc_plants/${item.id}/toggle-listing`);
      } catch (e) {
        item.is_listed = !item.is_listed; // revert on error
        console.error("Error toggling listing:", e);
      }
      this.togglingId = null;
    },
    openEdit(item) {
      this.editPlant = {
        ...item,
        mix_design_presets: item.mix_design_presets ? JSON.parse(JSON.stringify(item.mix_design_presets)) : {},
      };
      this.showEditDialog = true;
    },
    openAddGrade() {
      this.newGrade = { name: '', ingredients: {} };
      this.ingredientOrder.forEach(ing => { this.newGrade.ingredients[ing] = 0; });
      this.showAddGradeDialog = true;
    },
    submitAddGrade() {
      const grade = this.newGrade.name.trim().toUpperCase();
      if (!grade) return;
      if (!this.editPlant.mix_design_presets) this.$set(this.editPlant, 'mix_design_presets', {});
      const ingredients = {};
      this.ingredientOrder.forEach(ing => {
        const val = parseFloat(this.newGrade.ingredients[ing]);
        ingredients[ing] = isNaN(val) ? 0 : val; // keep 0 values (e.g. Microsilica for lower grades)
      });
      this.$set(this.editPlant.mix_design_presets, grade, ingredients);
      this.showAddGradeDialog = false;
    },
    removeGrade(grade) {
      this.$delete(this.editPlant.mix_design_presets, grade);
    },
    updateIngredient(grade, ing, val) {
      const num = parseFloat(val);
      if (!isNaN(num)) {
        this.$set(this.editPlant.mix_design_presets[grade], ing, num);
      }
    },
    async saveEdit() {
      this.isSaving = true;
      try {
        await axios.patch(`rmc_plants/${this.editPlant.id}`, {
          name: this.editPlant.name,
          city: this.editPlant.city,
          state: this.editPlant.state,
          address: this.editPlant.address,
          price_per_cubic_meter: this.editPlant.price_per_cubic_meter,
          commission_per_cubic_m: this.editPlant.commission_per_cubic_m ?? 0,
          transport_rate_per_m3: this.editPlant.transport_rate_per_m3 ?? 0,
          operating_start_time: this.editPlant.operating_start_time || '06:00',
          operating_end_time: this.editPlant.operating_end_time || '21:00',
          status: this.editPlant.status,
          total_capacity: this.editPlant.total_capacity,
          batching_capacity: this.editPlant.batching_capacity,
          is_listed: this.editPlant.is_listed,
          mix_design_presets: Object.keys(this.editPlant.mix_design_presets || {}).length > 0 ? this.editPlant.mix_design_presets : null,
        });
        this.showEditDialog = false;
        this.getData();
      } catch (e) {
        console.error("Error saving plant:", e);
      }
      this.isSaving = false;
    },
    async deletePlant(id) {
      if (!confirm("Are you sure you want to delete this plant?")) return;
      try {
        await axios.post(`rmc_plants/delete/${id}`);
        this.getData();
      } catch (e) {
        console.error("Error deleting plant:", e);
      }
    },
    formatNumber(num) {
      return new Intl.NumberFormat("en-IN").format(parseFloat(num) || 0);
    },
  },
};
</script>
